/**
 * Environment variables utility for the web application
 * This centralizes all environment variable access for better management
 */

import { z } from 'zod/v3';

// Environment type detection
export const isDevelopment = import.meta.env.DEV;
export const isProduction = import.meta.env.PROD;

// Environment validation
const _envSchema = z.object({
  DEV: z.boolean(),
  PROD: z.boolean(),
});

// Portless local dev (vercel-labs/portless) serves both apps over HTTPS on
// `*.screenata.localhost`. In a git worktree, `portless run` prepends the git
// branch (last segment) as a subdomain, so each worktree (which checks out a
// distinct branch) gets a distinct host, e.g.
// https://<branch>.web.screenata.localhost (and just web.screenata.localhost on
// main). When we detect we're on such a host, derive the API origin by swapping
// the `web` label for `api` so every worktree works with zero per-worktree edits.
const PORTLESS_BASE = 'screenata.localhost';
function resolvePortlessOrigins(): { server: string; web: string } | null {
  if (typeof window === 'undefined') return null;
  const { host, protocol } = window.location;
  const webSuffix = `web.${PORTLESS_BASE}`;
  if (!host.endsWith(webSuffix)) return null;
  const serverHost = `${host.slice(0, -webSuffix.length)}api.${PORTLESS_BASE}`;
  return { web: `${protocol}//${host}`, server: `${protocol}//${serverHost}` };
}

const portlessOrigins = resolvePortlessOrigins();

// RFC-203: Trust Center custom-domain mode. When the SPA is served on a
// customer's own domain (e.g. `trust.acme.com`), it must call the API
// SAME-ORIGIN — Caddy proxies `/api` + `/trpc` to the backend — because the
// server's general CORS policy is first-party-only and would reject an
// arbitrary custom-domain origin. This is OPT-IN and fail-safe: it only
// activates when `VITE_FIRST_PARTY_HOSTS` is configured AND the current host
// is not in that list (nor a localhost dev host). If the env var is unset we
// never treat any host as custom, so first-party behaviour is unchanged.
const FIRST_PARTY_HOSTS = (import.meta.env.VITE_FIRST_PARTY_HOSTS || '')
  .split(',')
  .map((h: string) => h.trim().toLowerCase())
  .filter(Boolean);

function resolveIsCustomDomainHost(): boolean {
  if (typeof window === 'undefined') return false;
  if (FIRST_PARTY_HOSTS.length === 0) return false; // opt-in guard
  const host = window.location.hostname.toLowerCase();
  if (host === 'localhost' || host.endsWith('.localhost')) return false;
  return !FIRST_PARTY_HOSTS.includes(host);
}

// True when the app is running on a Trust Center custom domain (not first-party).
export const IS_CUSTOM_DOMAIN_HOST = resolveIsCustomDomainHost();

/**
 * RFC-203 — SINGLE SOURCE OF TRUTH for which paths render as the PUBLIC Trust
 * Center "host-mode" landing on a customer custom domain (e.g. trust.acme.com).
 *
 * ⚠️ TWO call sites depend on this and MUST agree, or a custom-domain visitor
 * breaks. Both import this predicate rather than re-checking the path inline:
 *   1. `routes/__root.tsx` — renders <TrustCenterHostEntry/> in place of the
 *      <Outlet/> for these paths.
 *   2. `routes/(protected)/route.tsx` `beforeLoad` — SKIPS the auth guard for
 *      these paths, so an anonymous visitor isn't redirected to /login before
 *      the root can render the (public, no-session) Trust Center.
 * If the two ever disagree you get one of two bugs: a login-redirect (guard
 * stricter than root) or a blank <Outlet/> (root stricter than guard).
 *
 * Today the public surface is only the site root (`/`): the Trust Center's
 * internal tabs are in-memory React state and never change the pathname, and
 * `/trust/access/:token` + `/trust/resources/:id` are separate top-level public
 * routes that never hit the protected guard. ➜ If you EVER add a new PATH-based
 * public Trust Center page, extend the predicate here — both call sites and the
 * auth-guard bypass then update together automatically.
 */
export function isTrustCenterHostModePath(pathname: string): boolean {
  return IS_CUSTOM_DOMAIN_HOST && pathname === '/';
}

const customDomainOrigin =
  IS_CUSTOM_DOMAIN_HOST && typeof window !== 'undefined'
    ? window.location.origin
    : null;

// Server configuration
export const SERVER_URL = (customDomainOrigin || portlessOrigins?.server || import.meta.env.VITE_SERVER_URL || 'http://localhost:15001').replace(/\/$/, '');
export const WEB_URL = (customDomainOrigin || portlessOrigins?.web || import.meta.env.VITE_WEB_URL || 'http://localhost:5173').replace(/\/$/, '');
export const APP_NAME = (import.meta.env.VITE_APP_NAME || 'Screenata');
export const APP_PUBLIC_URL = (import.meta.env.VITE_APP_PUBLIC_URL || 'https://screenata.com');
// Landing-site page offering the desktop recorder builds. The artifacts
// themselves live on downloads.screenata.com and are linked from there —
// see docs/deployment/DESKTOP-RELEASE.md.
export const DESKTOP_DOWNLOAD_URL = `${APP_PUBLIC_URL.replace(/\/$/, '')}/desktop`;

// Add performance flags
export const PERFORMANCE_FLAGS = {
  // Disable WebSockets for better bfcache support and performance
  DISABLE_WEBSOCKETS: import.meta.env.VITE_DISABLE_WEBSOCKETS === 'true',
  // Disable real-time collaboration for better performance
  DISABLE_COLLABORATION: import.meta.env.VITE_DISABLE_COLLABORATION === 'true',
  // Enable aggressive lazy loading
  ENABLE_AGGRESSIVE_LAZY_LOADING: import.meta.env.VITE_ENABLE_AGGRESSIVE_LAZY_LOADING !== 'false',
  // Disable analytics/tracking for better performance
  DISABLE_ANALYTICS: import.meta.env.VITE_DISABLE_ANALYTICS === 'true',
} as const;

// Kill switch for the in-app guided tours / coach marks (`useCoachTour`).
// The tour renders a full-screen overlay that intercepts clicks, so e2e runs
// start the dev/preview server with `VITE_DISABLE_TOUR=true` to keep it out of
// the way. When set, tours never auto-open and the "Show me how" affordances
// that gate on `hasApplicableSteps` disappear.
export const TOUR_DISABLED = import.meta.env.VITE_DISABLE_TOUR === 'true';

// Release flag for the inbound email interface ("Email Vera" settings page).
// Off unless explicitly enabled; pair with the server's `EMAIL_INTERFACE_ENABLED`.
export const EMAIL_INTERFACE_ENABLED = import.meta.env.VITE_EMAIL_INTERFACE_ENABLED === 'true';

export const EXTENSION_ID = import.meta.env.VITE_BROWSER_EXTENSION_ID;

// Allowlist of Chrome extension IDs permitted to receive an auth token via the
// login OAuth redirect. Supports multiple published extensions (e.g. the
// recorder + the standalone questionnaire extension, #1367) — each passes its
// own `<id>.chromiumapp.org` redirect_uri and must be listed here in prod.
// Sources: the legacy single `VITE_BROWSER_EXTENSION_ID` plus a comma-separated
// `VITE_BROWSER_EXTENSION_IDS`. Deduped, empties dropped.
export const EXTENSION_IDS: string[] = Array.from(
  new Set(
    [
      import.meta.env.VITE_BROWSER_EXTENSION_ID,
      ...String(import.meta.env.VITE_BROWSER_EXTENSION_IDS ?? '').split(','),
    ]
      .map((id) => (id ?? '').trim())
      .filter(Boolean)
  )
);

// API endpoints
export const apiEndpoints = {
  // Server Root URL
  root: SERVER_URL,
  // API root
  api: `${SERVER_URL}/api`,
  // Regular chat endpoint
  chat: `${SERVER_URL}/api/ai/chat`,
  // Screenata-specific compliance chat endpoint
  screenataChat: `${SERVER_URL}/api/ai/screenata-chat`,
  /**
   * @deprecated RFC-160 Phase 2 — the wizard chat rail now targets
   * `screenataChat` with `chatKind: 'onboarding-wizard'` in the request body.
   * Remaining references from `VeraWizardChatPanel` are in the
   * legacy-safety-net component that retires in the next cleanup pass.
   */
  wizardChat: `${SERVER_URL}/api/ai/wizard-chat`,
  // AI-driven onboarding chat (replaces wizard with conversational flow)
  onboardingChat: `${SERVER_URL}/api/ai/onboarding-chat`,
  // Auditor-specific chat endpoint (read-only, cycle-scoped)
  auditorChat: `${SERVER_URL}/api/ai/auditor-chat`,
  // Screenata test-plan enhancement streaming endpoint
  screenataTestEnhance: `${SERVER_URL}/api/ai/screenata-test-enhance`,
  // Stream article endpoint
  streamArticle: `${SERVER_URL}/api/ai/flow/generate`,
  
  // TRPC API endpoint
  trpcApi: `${SERVER_URL}/trpc`,
};

// Function to get API URL with optional path
export const getApiUrl = (path: string): string => {
  return `${SERVER_URL}${path.startsWith('/') ? path : `/${path}`}`;
};
